import { createApi } from '@reduxjs/toolkit/query/react'
import { createNeighborhoodAwareBaseQuery } from '../../../Utils/baseQuery'

// This API handles the 19th Hole forum — posts and comments that
// residents create in the neighborhood feed. Admins can delete any
// post or comment from here when content needs moderation.
export const forumApiService = createApi({
  reducerPath: 'forumApi',
  baseQuery: createNeighborhoodAwareBaseQuery('/forum/'),
  tagTypes: ['ForumPost', 'ForumComment'],
  endpoints: (builder) => ({
    // Get the paginated list of EVERY post in this neighborhood — active,
    // hidden, and deleted alike. This hits the admin-only endpoint so the
    // moderation table sees all content regardless of status (the public
    // /forum/posts endpoint only returns active posts and is scoped to the
    // resident's own groups, which is wrong for moderation).
    // Params are page / rowsPerPage / search to match the backend contract.
    getForumPosts: builder.query({
      query: (args) => ({
        url: 'admin/posts',
        method: 'GET',
        params: args,
      }),
      providesTags: ['ForumPost'],
      keepUnusedDataFor: 0,
    }),

    // Get the paginated list of EVERY comment in this neighborhood, newest
    // first. Comments are NOT embedded in the posts payload, so the Comments
    // tab needs its own admin endpoint to list them. Each comment carries its
    // parent post (postId.title) so the table can show which post it's on.
    // Params are page / rowsPerPage / search to match the backend contract.
    getForumComments: builder.query({
      query: (args) => ({
        url: 'admin/comments',
        method: 'GET',
        params: args,
      }),
      providesTags: ['ForumComment'],
      keepUnusedDataFor: 0,
    }),

    // Get a single post plus all of its comments.
    // Useful if we ever need a detail view before deleting.
    getPostDetail: builder.query({
      query: (postId) => ({
        url: `post/${postId}`,
        method: 'GET',
      }),
      providesTags: ['ForumPost', 'ForumComment'],
      keepUnusedDataFor: 0,
    }),

    // Permanently delete a post (and its comments) as admin.
    // Matches backend contract: DELETE /forum/admin/post/:id
    adminDeletePost: builder.mutation({
      query: (postId) => ({
        url: `admin/post/${postId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ForumPost'],
    }),

    // Permanently delete a single comment as admin.
    // Matches backend contract: DELETE /forum/admin/comment/:id
    adminDeleteComment: builder.mutation({
      query: (commentId) => ({
        url: `admin/comment/${commentId}`,
        method: 'DELETE',
      }),
      // Invalidate both so a comment delete also refreshes the parent post list
      invalidatesTags: ['ForumComment', 'ForumPost'],
    }),
  }),
})

export const {
  useGetForumPostsQuery,
  useGetForumCommentsQuery,
  useGetPostDetailQuery,
  useAdminDeletePostMutation,
  useAdminDeleteCommentMutation,
} = forumApiService
